import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { FacturationSkeleton } from './ui/Skeleton';
import { useAppConfig } from '../context/AppConfigContext';
import BillingSettingsModal from './BillingSettingsModal';
import { generateClientInvoicePDF } from '../utils/generateClientInvoicePDF';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FacturationRecap = () => {
  const { appConfig, refreshConfig } = useAppConfig();
  const companyName = appConfig?.company_name || 'Gestion principale';
  
  // Modal Paramètres de facturation
  const [showBillingSettings, setShowBillingSettings] = useState(false);
  const [searchParams] = useSearchParams();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // Lire le filtre owner depuis l'URL ou utiliser '' par défaut
  const [filterOwner, setFilterOwner] = useState(searchParams.get('owner') || '');
  
  // Gestionnaires du tenant (chargés dynamiquement)
  const [proprietaires, setProprietaires] = useState({});
  
  // Config de facturation (billing settings) — fetchée directement pour éviter les problèmes
  // de synchronisation du contexte global lors de l'impersonation Super Admin.
  const [billingConfig, setBillingConfig] = useState(null);
  
  // État pour gérer les logements facturés et accordions
  const [facturedLogements, setFacturedLogements] = useState({});
  const [expandedLogements, setExpandedLogements] = useState({});
  
  // États pour le ramassage de sacs
  const [ramassageSacs, setRamassageSacs] = useState([]);
  const [showRamassageForm, setShowRamassageForm] = useState(false);
  const [editingRamassage, setEditingRamassage] = useState(null);
  const [ramassageForm, setRamassageForm] = useState({
    date: new Date().toISOString().split('T')[0],
    quantite: '',
    prix_unitaire: ''
  });

  useEffect(() => {
    loadRecap();
    loadRamassageSacs();
  }, [selectedMonth, selectedYear, filterOwner]);

  // Charger les gestionnaires du tenant + config de facturation (dynamique)
  useEffect(() => {
    (async () => {
      try {
        const [propRes, cfgRes] = await Promise.all([
          axios.get(`${API}/proprietaires-config`).catch(() => ({ data: {} })),
          axios.get(`${API}/setup/config`).catch(() => ({ data: null })),
        ]);
        setProprietaires(propRes.data || {});
        setBillingConfig(cfgRes.data);
      } catch (e) {
        console.error("Erreur chargement config facturation:", e);
      }
    })();
  }, []);
  
  // Charger les ramassages de sacs
  const loadRamassageSacs = async () => {
    try {
      const response = await axios.get(`${API}/ramassage-sacs?year=${selectedYear}&month=${selectedMonth}`);
      setRamassageSacs(response.data);
    } catch (error) {
      console.error("Erreur chargement ramassages:", error);
    }
  };
  
  // Créer un ramassage
  const handleCreateRamassage = async () => {
    if (!ramassageForm.quantite || !ramassageForm.prix_unitaire) {
      toast.info("Veuillez remplir tous les champs");
      return;
    }
    try {
      await axios.post(`${API}/ramassage-sacs`, {
        date: ramassageForm.date,
        quantite: parseInt(ramassageForm.quantite),
        prix_unitaire: parseFloat(ramassageForm.prix_unitaire)
      });
      loadRamassageSacs();
      setShowRamassageForm(false);
      setRamassageForm({ date: new Date().toISOString().split('T')[0], quantite: '', prix_unitaire: '' });
    } catch (error) {
      console.error("Erreur création ramassage:", error);
      toast.info("Erreur lors de la création");
    }
  };
  
  // Modifier un ramassage
  const handleUpdateRamassage = async () => {
    if (!editingRamassage) return;
    try {
      await axios.put(`${API}/ramassage-sacs/${editingRamassage.id}`, {
        quantite: parseInt(ramassageForm.quantite),
        prix_unitaire: parseFloat(ramassageForm.prix_unitaire)
      });
      loadRamassageSacs();
      setEditingRamassage(null);
      setRamassageForm({ date: new Date().toISOString().split('T')[0], quantite: '', prix_unitaire: '' });
    } catch (error) {
      console.error("Erreur modification ramassage:", error);
      toast.info("Erreur lors de la modification");
    }
  };
  
  // Supprimer un ramassage
  const handleDeleteRamassage = async (id) => {
    try {
      await axios.delete(`${API}/ramassage-sacs/${id}`);
      loadRamassageSacs();
    } catch (error) {
      console.error("Erreur suppression:", error);
    }
  };
  
  // Ouvrir le formulaire d'édition
  const openEditRamassage = (ramassage) => {
    setEditingRamassage(ramassage);
    setRamassageForm({
      date: ramassage.date,
      quantite: ramassage.quantite.toString(),
      prix_unitaire: ramassage.prix_unitaire.toString()
    });
  };
  
  // Calculer le total des ramassages
  const totalRamassageSacs = ramassageSacs.reduce((sum, r) => sum + (r.montant_total || 0), 0);
  const totalSacs = ramassageSacs.reduce((sum, r) => sum + (r.quantite || 0), 0);
  
  // Fonction pour rendre le tableau ramassage sacs
  const renderRamassageSacsTable = () => (
    <div className="card border-2 border-amber-300 mt-3 mb-3">
      <div className="bg-gradient-to-r from-amber-100 to-amber-200 px-4 py-3 border-b border-amber-300">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/images/camionnette.png" alt="Camionnette" className="w-10 h-10 object-contain" />
            <div>
              <h3 className="text-base font-bold text-amber-900">Ramassage sacs de linge – Garage Keys</h3>
              <p className="text-sm text-amber-700">{totalSacs} sacs • {ramassageSacs.length} ramassage(s)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xl font-bold text-amber-700">{totalRamassageSacs.toFixed(2)}€</p>
              <p className="text-xs text-amber-600">Total du mois</p>
            </div>
            <button
              onClick={() => {
                setShowRamassageForm(true);
                setEditingRamassage(null);
                setRamassageForm({ date: new Date().toISOString().split('T')[0], quantite: '', prix_unitaire: '' });
              }}
              className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold text-xl"
              title="Ajouter un ramassage"
            >
              +
            </button>
          </div>
        </div>
      </div>
      
      {/* Formulaire ajout/édition */}
      {(showRamassageForm || editingRamassage) && (
        <div className="p-4 bg-amber-50 border-b border-amber-200">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={ramassageForm.date}
                onChange={(e) => setRamassageForm({...ramassageForm, date: e.target.value})}
                disabled={!!editingRamassage}
                className="form-input px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantité de sacs</label>
              <input
                type="number"
                min="0"
                value={ramassageForm.quantite}
                onChange={(e) => setRamassageForm({...ramassageForm, quantite: e.target.value})}
                placeholder="Nb sacs"
                className="form-input px-3 py-2 border rounded-lg w-28"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix unitaire (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={ramassageForm.prix_unitaire}
                onChange={(e) => setRamassageForm({...ramassageForm, prix_unitaire: e.target.value})}
                placeholder="€/sac"
                className="form-input px-3 py-2 border rounded-lg w-28"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Montant total</label>
              <div className="px-3 py-2 bg-white border rounded-lg w-28 text-center font-bold text-green-600">
                {((parseFloat(ramassageForm.quantite) || 0) * (parseFloat(ramassageForm.prix_unitaire) || 0)).toFixed(2)}€
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={editingRamassage ? handleUpdateRamassage : handleCreateRamassage}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium"
              >
                {editingRamassage ? '✓ Modifier' : '✓ Ajouter'}
              </button>
              <button
                onClick={() => {
                  setShowRamassageForm(false);
                  setEditingRamassage(null);
                  setRamassageForm({ date: new Date().toISOString().split('T')[0], quantite: '', prix_unitaire: '' });
                }}
                className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Tableau des ramassages */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Date</th>
              <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Quantité</th>
              <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Prix unitaire</th>
              <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Montant total</th>
              <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {ramassageSacs.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                  Aucun ramassage ce mois-ci. Cliquez sur + pour ajouter.
                </td>
              </tr>
            ) : (
              ramassageSacs.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">
                    {new Date(r.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </td>
                  <td className="px-4 py-2 text-center font-medium">{r.quantite} sacs</td>
                  <td className="px-4 py-2 text-center">{r.prix_unitaire?.toFixed(2)}€</td>
                  <td className="px-4 py-2 text-center font-bold text-green-600">{r.montant_total?.toFixed(2)}€</td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => openEditRamassage(r)}
                        className="px-2 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteRamassage(r.id)}
                        className="px-2 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {ramassageSacs.length > 0 && (
            <tfoot className="bg-amber-100">
              <tr>
                <td className="px-4 py-2 font-bold">TOTAL</td>
                <td className="px-4 py-2 text-center font-bold">{totalSacs} sacs</td>
                <td className="px-4 py-2 text-center">-</td>
                <td className="px-4 py-2 text-center font-bold text-green-700">{totalRamassageSacs.toFixed(2)}€</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );

  // Charger les états facturés depuis la base de données
  useEffect(() => {
    const loadFacturationStatus = async () => {
      try {
        const response = await axios.get(`${API}/facturation/status/${selectedYear}/${selectedMonth}`);
        setFacturedLogements(response.data.statuses || {});
      } catch (error) {
        console.error("Erreur chargement statuts facturation:", error);
        setFacturedLogements({});
      }
    };
    
    loadFacturationStatus();
    // Par défaut, tous les logements sont dépliés
    setExpandedLogements({});
  }, [selectedYear, selectedMonth]);

  const loadRecap = async () => {
    setLoading(true);
    try {
      let url = `${API}/facturation/monthly-recap/${selectedYear}/${selectedMonth}`;
      if (filterOwner) {
        url += `?owner_filter=${filterOwner}`;
      }
      const response = await axios.get(url);
      setRecap(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement du récapitulatif:", error);
      toast.info("Erreur lors du chargement du récapitulatif");
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle facturé pour un logement
  const toggleFactured = async (clientId, housingId) => {
    const key = `${clientId}_${housingId}`;
    const newFactured = {
      ...facturedLogements,
      [key]: !facturedLogements[key]
    };
    setFacturedLogements(newFactured);
    
    // Sauvegarder dans la base de données
    try {
      await axios.put(`${API}/facturation/status/${selectedYear}/${selectedMonth}/toggle`, null, {
        params: { client_id: clientId, housing_id: housingId }
      });
    } catch (error) {
      console.error("Erreur sauvegarde statut facturation:", error);
      // Rollback en cas d'erreur
      setFacturedLogements(facturedLogements);
    }
    
    // Si on marque comme facturé, replier le logement
    if (newFactured[key]) {
      setExpandedLogements(prev => ({ ...prev, [key]: false }));
    }
  };
  
  // Toggle accordion pour un logement
  const toggleExpanded = (clientId, housingId) => {
    const key = `${clientId}_${housingId}`;
    setExpandedLogements(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  // Vérifier si un logement est déplié (par défaut: oui, sauf si facturé)
  const isExpanded = (clientId, housingId) => {
    const key = `${clientId}_${housingId}`;
    if (expandedLogements[key] !== undefined) {
      return expandedLogements[key];
    }
    // Par défaut: déplié sauf si facturé
    return !facturedLogements[key];
  };
  
  // État pour le modal d'édition
  const [editModal, setEditModal] = useState({ open: false, client: null, logement: null });
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Ouvrir le modal d'édition
  const openEditModal = (client, logement) => {
    setEditData({
      // Infos ménages
      nb_menages: logement.nb_menages || 0,
      prix_menages_total: logement.prix_menages_total || 0,
      prix_unitaire_menage: logement.nb_menages > 0 ? (logement.prix_menages_total / logement.nb_menages).toFixed(2) : 0,
      
      // Parures
      nb_parures_total: logement.nb_parures_total || 0,
      tarif_parures_total: logement.tarif_parures_total || 0,
      
      // Consommables
      total_consommables: logement.total_consommables || 0,
      consommables_details: logement.consommables_details || [],
      
      // Prestations
      total_prestations: logement.total_prestations || 0,
      prestations: logement.prestations || [],
      
      // Ajustement manuel
      ajustement: 0,
      commentaire_ajustement: '',
      
      // Total original (pour calcul différence)
      total_original: logement.total_logement || 0,
      
      // Total
      total_logement: logement.total_logement || 0
    });
    setEditModal({ open: true, client, logement });
  };
  
  // Calculer le nouveau total
  const calculateTotal = (data) => {
    const menages = parseFloat(data.prix_menages_total) || 0;
    const parures = parseFloat(data.tarif_parures_total) || 0;
    const consommables = parseFloat(data.total_consommables) || 0;
    const prestations = parseFloat(data.total_prestations) || 0;
    const ajustement = parseFloat(data.ajustement) || 0;
    return (menages + parures + consommables + prestations + ajustement).toFixed(2);
  };
  
  // Calculer la différence avec le total original
  const calculateDifference = (data) => {
    const newTotal = parseFloat(calculateTotal(data)) || 0;
    const originalTotal = parseFloat(data.total_original) || 0;
    return (newTotal - originalTotal).toFixed(2);
  };
  
  // Sauvegarder les modifications
  const saveEdits = async () => {
    setSaving(true);
    try {
      const editKey = `${editModal.client.client_id || editModal.client.client_name}_${editModal.logement.housing_id}`;
      const editValue = {
        ...editData,
        total_logement: calculateTotal(editData),
        modified_at: new Date().toISOString()
      };
      
      // Sauvegarder dans la base de données
      const statusResponse = await axios.get(`${API}/facturation/status/${selectedYear}/${selectedMonth}`);
      const currentEdits = statusResponse.data.edits || {};
      currentEdits[editKey] = editValue;
      
      await axios.post(`${API}/facturation/status/${selectedYear}/${selectedMonth}`, {
        statuses: statusResponse.data.statuses || {},
        edits: currentEdits
      });
      
      // Mettre à jour le recap localement
      const updatedRecap = { ...recap };
      const clientIdx = updatedRecap.clients.findIndex(c => 
        (c.client_id || c.client_name) === (editModal.client.client_id || editModal.client.client_name)
      );
      if (clientIdx !== -1) {
        const logementIdx = updatedRecap.clients[clientIdx].logements.findIndex(l => 
          l.housing_id === editModal.logement.housing_id
        );
        if (logementIdx !== -1) {
          updatedRecap.clients[clientIdx].logements[logementIdx] = {
            ...updatedRecap.clients[clientIdx].logements[logementIdx],
            ...editData,
            total_logement: parseFloat(calculateTotal(editData))
          };
          
          // Recalculer le total client
          updatedRecap.clients[clientIdx].total_client = updatedRecap.clients[clientIdx].logements.reduce(
            (sum, l) => sum + parseFloat(l.total_logement || 0), 0
          ).toFixed(2);
        }
      }
      
      // Recalculer le total général
      updatedRecap.total_general = updatedRecap.clients.reduce(
        (sum, c) => sum + parseFloat(c.total_client || 0), 0
      ).toFixed(2);
      
      setRecap(updatedRecap);
      setEditModal({ open: false, client: null, logement: null });
      toast.success("Modifications enregistrées");
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  // Flag pour éviter les boucles infinies lors du chargement des éditions
  const [editsLoaded, setEditsLoaded] = useState(false);
  
  // Charger les modifications sauvegardées depuis la base de données
  useEffect(() => {
    const loadEdits = async () => {
      if (recap && !editsLoaded) {
        try {
          const response = await axios.get(`${API}/facturation/status/${selectedYear}/${selectedMonth}`);
          const savedEdits = response.data.edits || {};
          
          if (Object.keys(savedEdits).length > 0) {
            const updatedRecap = { ...recap };
            updatedRecap.clients.forEach((client, cIdx) => {
              client.logements.forEach((logement, lIdx) => {
                const editKey = `${client.client_id || client.client_name}_${logement.housing_id}`;
                if (savedEdits[editKey]) {
                  updatedRecap.clients[cIdx].logements[lIdx] = {
                    ...logement,
                    ...savedEdits[editKey]
                  };
                }
              });
              // Recalculer total client
              updatedRecap.clients[cIdx].total_client = updatedRecap.clients[cIdx].logements.reduce(
                (sum, l) => sum + parseFloat(l.total_logement || 0), 0
              ).toFixed(2);
            });
            // Recalculer total général
            updatedRecap.total_general = updatedRecap.clients.reduce(
              (sum, c) => sum + parseFloat(c.total_client || 0), 0
            ).toFixed(2);
            setRecap(updatedRecap);
          }
          setEditsLoaded(true);
        } catch (error) {
          console.error("Erreur chargement éditions:", error);
        }
      }
    };
    
    loadEdits();
  }, [recap, editsLoaded, selectedYear, selectedMonth]);
  
  // Réinitialiser le flag quand le mois/année change
  useEffect(() => {
    setEditsLoaded(false);
  }, [selectedYear, selectedMonth]);

  const handleExportCSV = () => {
    if (!recap || !recap.clients) return;

    let csv = "Client,Logement,Nb Ménages,Prix Ménages,Nb Parures,Écart Parures,Tarif Parures,Total Consommables,Total Prestations,TOTAL\n";

    recap.clients.forEach(client => {
      client.logements.forEach(logement => {
        csv += `"${client.client_name}","${logement.logement_name}",${logement.nb_menages},${logement.prix_menages_total},${logement.nb_parures_total},${logement.ecart_parures},${logement.tarif_parures_total},${logement.total_consommables},${logement.total_prestations},${logement.total_logement}\n`;
      });
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facturation_${monthNames[selectedMonth - 1]}_${selectedYear}.csv`;
    a.click();
  };

  const handleExportPDF = () => {
    if (!recap || !recap.clients) {
      toast.error("Aucune donnée à exporter.");
      return;
    }

    try {
      const doc = new jsPDF();
      
      // Titre
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(`${companyName} - Facturation Mensuelle`, 105, 20, { align: 'center' });
      
      // Période
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`${monthNames[selectedMonth - 1]} ${selectedYear}`, 105, 30, { align: 'center' });
      
      // Synthèse
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Synthèse Globale', 14, 45);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Chiffre d'Affaires Total: ${recap.total_general.toFixed(2)}€`, 14, 55);
      doc.text(`Nombre de clients: ${recap.clients.length}`, 14, 62);
      
      // Tableau détaillé par client
      let yPos = 75;
      
      recap.clients.forEach((client, index) => {
        // Vérifier si on doit ajouter une nouvelle page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        // Nom du client
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`${client.client_name} - Total: ${client.total_client.toFixed(2)}€`, 14, yPos);
        yPos += 5;
        
        // Tableau des logements
        const logementData = client.logements.map(log => [
          log.logement_name,
          log.nb_menages,
          log.prix_menages_total.toFixed(2) + '€',
          log.nb_parures_total,
          log.tarif_parures_total.toFixed(2) + '€',
          log.total_consommables.toFixed(2) + '€',
          log.total_logement.toFixed(2) + '€'
        ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Logement', 'Ménages', 'Prix', 'Parures', 'Tarif P.', 'Conso.', 'TOTAL']],
          body: logementData,
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
          styles: { fontSize: 8 },
          margin: { left: 14 }
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
      });
      
      // Total général en bas
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(59, 130, 246);
      doc.rect(14, yPos, 182, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(`TOTAL GÉNÉRAL: ${recap.total_general.toFixed(2)}€`, 105, yPos + 7, { align: 'center' });
      
      // Sauvegarder
      const filename = `facturation_${monthNames[selectedMonth - 1]}_${selectedYear}.pdf`;
      doc.save(filename);
      
      toast.success("PDF généré avec succès !");
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-3 px-4" data-testid="facturation-loading">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">💰 Facturation Mensuelle</h1>
          <p className="text-gray-600">Chargement...</p>
        </div>
        <FacturationSkeleton />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-3 px-4">
      {/* Header */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">💰 Facturation Mensuelle</h1>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowBillingSettings(true)}
                className="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
                data-testid="billing-settings-open-btn"
                title="Coordonnées émetteur des factures PDF"
              >
                ⚙️ Paramètres
              </button>
              <button
                onClick={handleExportPDF}
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!recap || !recap.clients || recap.clients.length === 0}
              >
                📄 Exporter PDF
              </button>
              <button
                onClick={handleExportCSV}
                className="btn-secondary"
                disabled={!recap || !recap.clients || recap.clients.length === 0}
              >
                📥 Exporter CSV
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="form-input"
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="form-input"
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
              <select
                value={filterOwner}
                onChange={(e) => setFilterOwner(e.target.value)}
                className="form-input"
                data-testid="facturation-owner-filter"
              >
                <option value="">{companyName}</option>
                {Object.entries(proprietaires).map(([key, info]) => (
                  <option key={key} value={key}>Gestion {info.nom}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Titre Facturation */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800">
          🏠 Facturation {filterOwner && proprietaires[filterOwner] ? `Gestion ${proprietaires[filterOwner].nom}` : companyName}
        </h2>
      </div>

      {/* Récapitulatif */}
      {recap && (
        <div className="space-y-3">
          {/* Total général */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-4 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold">Total Général : {recap.total_general}€</h2>
            <p className="text-green-100 mt-1">
              {monthNames[selectedMonth - 1]} {selectedYear}
            </p>
          </div>

          {/* Par client */}
          {recap.clients && recap.clients.length > 0 ? (
            recap.clients.map((client) => (
              <React.Fragment key={client.client_id}>
              <div className="card">
                {/* En-tête client */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2.5 border-b border-gray-200">
                  <div className="flex justify-between items-center gap-3">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{client.client_name}</h2>
                      <p className="text-sm text-gray-600">{client.logements.length} logement(s)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {/* Calculer le total client avec ramassage sacs pour Fred Keys uniquement */}
                        {(() => {
                          const isFredKeys = client.client_name && client.client_name.toUpperCase() === 'FRED KEYS';
                          const totalAvecRamassage = isFredKeys 
                            ? parseFloat(client.total_client) + totalRamassageSacs 
                            : parseFloat(client.total_client);
                          
                          return (
                            <>
                              <p className="text-2xl font-bold text-blue-600">{totalAvecRamassage.toFixed(2)}€</p>
                              {isFredKeys && totalRamassageSacs > 0 && (
                                <p className="text-xs text-gray-500">
                                  dont {totalRamassageSacs.toFixed(2)}€ ramassage sacs
                                </p>
                              )}
                              <p className="text-xs text-gray-500">Total client</p>
                            </>
                          );
                        })()}
                      </div>
                      <button
                        onClick={async () => {
                          const activeCfg = billingConfig || appConfig;
                          if (!activeCfg?.billing_company_name && !activeCfg?.company_name) {
                            toast.error("Renseignez d'abord les Paramètres de facturation");
                            setShowBillingSettings(true);
                            return;
                          }
                          try {
                            await generateClientInvoicePDF({
                              appConfig: activeCfg,
                              client,
                              year: selectedYear,
                              month: selectedMonth,
                            });
                            toast.success(`Facture PDF générée pour ${client.client_name}`);
                          } catch (e) {
                            toast.error(`Erreur : ${e.message}`);
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                        data-testid={`generate-invoice-btn-${client.client_id || client.client_name}`}
                        title="Générer et télécharger la facture PDF de ce client"
                      >
                        📄 Facture PDF
                      </button>
                    </div>
                  </div>
                </div>

                {/* Logements du client */}
                <div className="card-body space-y-3 py-3">
                  {/* Tableau ramassage sacs EN PREMIER pour FRED KEYS uniquement (pas FREDERIC OPERA etc.) */}
                  {client.client_name && client.client_name.toUpperCase() === 'FRED KEYS' && renderRamassageSacsTable()}
                  
                  {client.logements.map((logement) => {
                    const isFactured = facturedLogements[`${client.client_id || client.client_name}_${logement.housing_id}`];
                    const expanded = isExpanded(client.client_id || client.client_name, logement.housing_id);
                    
                    return (
                    <div 
                      key={logement.logement_id} 
                      className={`border-2 rounded-lg overflow-hidden transition-all duration-300 ${
                        isFactured 
                          ? 'border-green-400 bg-green-50' 
                          : 'border-gray-200'
                      }`}
                    >
                      {/* En-tête logement - Cliquable pour accordion */}
                      <div 
                        className={`px-3 py-2 cursor-pointer transition-all duration-300 ${
                          isFactured 
                            ? 'bg-gradient-to-r from-green-100 to-green-200 border-b-2 border-green-300' 
                            : 'bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200'
                        }`}
                        onClick={() => toggleExpanded(client.client_id || client.client_name, logement.housing_id)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className={`transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`}>
                              ▶
                            </span>
                            <div>
                              <h3 className={`text-lg font-bold ${isFactured ? 'text-green-800' : 'text-gray-900'}`}>
                                {isFactured && '✅ '}{logement.logement_name}
                              </h3>
                              <p className={`text-sm ${isFactured ? 'text-green-600' : 'text-gray-600'}`}>
                                {logement.logement_address}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className={`text-2xl font-bold ${isFactured ? 'text-green-700' : 'text-gray-900'}`}>
                                {logement.total_logement}€
                              </p>
                              <p className={`text-xs ${isFactured ? 'text-green-600' : 'text-gray-500'}`}>
                                {isFactured ? 'Facturé ✓' : 'Total logement'}
                              </p>
                            </div>
                            {/* Bouton Modifier */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(client, logement);
                              }}
                              className="px-3 py-2 rounded-lg font-semibold text-sm bg-blue-500 text-white hover:bg-blue-600 transition-all"
                            >
                              ✏️ Modifier
                            </button>
                            {/* Bouton Facturé */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFactured(client.client_id || client.client_name, logement.housing_id);
                              }}
                              className={`px-3 py-2 rounded-lg font-semibold text-sm transition-all ${
                                isFactured
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {isFactured ? '✅ Facturé' : '💰 Facturer'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Détail facturation - Accordion */}
                      <div className={`transition-all duration-300 overflow-hidden ${expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="p-3 space-y-2">
                        {/* Ménages */}
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-bold text-blue-900">🧹 Ménages</h4>
                              <p className="text-sm text-blue-700 mt-0.5">
                                {logement.nb_menages} intervention(s) effectuée(s)
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-blue-900">{logement.prix_menages_total}€</p>
                            </div>
                          </div>
                          {/* Dates d'interventions avec intervenants */}
                          {logement.dates_interventions && logement.dates_interventions.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-blue-200">
                              <p className="text-xs font-semibold text-blue-800 mb-1">📅 Dates d'interventions :</p>
                              <div className="flex flex-wrap gap-1">
                                {logement.dates_interventions.map((item, idx) => {
                                  const dateStr = typeof item === 'object' ? item.date : item;
                                  const intervenant = typeof item === 'object' ? item.intervenant : null;
                                  return (
                                    <span 
                                      key={idx} 
                                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                                      title={intervenant ? `Intervenant: ${intervenant}` : ''}
                                    >
                                      {new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                      {intervenant && (
                                        <span className="text-blue-600 font-medium">
                                          ({intervenant.split(' ')[0]})
                                        </span>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Parures Sales */}
                        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                          {logement.type_client === 'professionnel' || logement.gestion_parures === false ? (
                            <div>
                              <h4 className="font-bold text-purple-900">🛏️ Parures Sales</h4>
                              <div className="mt-2 bg-blue-50 rounded p-2 border border-blue-200">
                                <p className="text-sm text-blue-800">
                                  ℹ️ {logement.type_client === 'professionnel' 
                                    ? 'Client professionnel - La gestion des parures est automatiquement désactivée pour les lieux professionnels.'
                                    : 'Pas de gestion de parures pour ce logement.'}
                                </p>
                              </div>
                            </div>
                          ) : (
                          <>
                          <div className="flex justify-between items-center mb-1.5">
                            <div>
                              <h4 className="font-bold text-purple-900">🛏️ Parures Sales</h4>
                              <p className="text-sm text-purple-700 mt-0.5">
                                {logement.nb_parures_total} parure(s) collectée(s)
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-purple-900">{logement.tarif_parures_total}€</p>
                            </div>
                          </div>
                          
                          {logement.parures_incluses ? (
                            <div className="mt-2 pt-2 border-t border-purple-200">
                              <div className="bg-purple-100 rounded p-2 text-center">
                                <p className="text-sm font-semibold text-purple-900">
                                  ✅ Inclus dans le tarif ménage
                                </p>
                                <p className="text-xs text-purple-700 mt-1">
                                  Les parures sont comprises dans le prix du ménage
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 pt-2 border-t border-purple-200 text-sm space-y-0.5">
                              <div className="flex justify-between text-purple-700">
                                <span>Nombre attendu ({logement.nb_menages} × {logement.nb_parures_defaut || 0}):</span>
                                <span className="font-medium">{logement.nb_menages * (logement.nb_parures_defaut || 0)}</span>
                              </div>
                              <div className="flex justify-between text-purple-700">
                                <span>Nombre réel:</span>
                                <span className="font-medium">{logement.nb_parures_total}</span>
                              </div>
                              <div className="flex justify-between font-bold">
                                <span>Écart:</span>
                                <span className={logement.ecart_parures > 0 ? 'text-green-600' : logement.ecart_parures < 0 ? 'text-red-600' : 'text-gray-600'}>
                                  {logement.ecart_parures > 0 ? '+' : ''}{logement.ecart_parures}
                                </span>
                              </div>
                            </div>
                          )}
                          </>
                          )}
                        </div>

                        {/* Consommables */}
                        {logement.consommables_details && logement.consommables_details.length > 0 && (
                          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="font-bold text-yellow-900">🧴 Produits Consommables</h4>
                              <p className="text-xl font-bold text-yellow-900">{logement.total_consommables}€</p>
                            </div>
                            <div className="space-y-2">
                              {logement.consommables_details.map((conso, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-yellow-800 items-center">
                                  <div>
                                    <span className="font-medium">{conso.nom}</span>
                                    <span className="text-xs text-yellow-600 ml-2">
                                      ({new Date(conso.date).toLocaleDateString('fr-FR')})
                                    </span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-xs text-yellow-600 mr-2">
                                      {conso.quantite} × {conso.prix_unitaire.toFixed(2)}€
                                    </span>
                                    <span className="font-medium">{conso.total.toFixed(2)}€</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Prestations supplémentaires */}
                        {logement.prestations && logement.prestations.length > 0 && (
                          <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-bold text-pink-900">🔧 Prestations Supplémentaires</h4>
                              <p className="text-xl font-bold text-pink-900">{logement.total_prestations}€</p>
                            </div>
                            <div className="space-y-2">
                              {logement.prestations.map((presta, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-pink-800">
                                  <span>{presta.type}</span>
                                  <span className="font-medium">{presta.tarif.toFixed(2)}€</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
              </React.Fragment>
            ))
          ) : (
            <div className="card">
              <div className="card-body text-center text-gray-500 py-8">
                Aucune donnée de facturation pour ce mois
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Modal d'édition */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-xl">
              <h2 className="text-xl font-bold">✏️ Modifier la facturation</h2>
              <p className="text-blue-100 text-sm">
                {editModal.client?.client_name} - {editModal.logement?.logement_name}
              </p>
            </div>
            
            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Section Ménages */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-bold text-blue-900 mb-3">🧹 Ménages</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Nombre de ménages</label>
                    <input
                      type="number"
                      value={editData.nb_menages}
                      onChange={(e) => setEditData({...editData, nb_menages: e.target.value === '' ? '' : parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Total ménages (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editData.prix_menages_total}
                      onChange={(e) => setEditData({...editData, prix_menages_total: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section Parures */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-bold text-purple-900 mb-3">🛏️ Parures</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-1">Nombre de parures</label>
                    <input
                      type="number"
                      value={editData.nb_parures_total}
                      onChange={(e) => setEditData({...editData, nb_parures_total: e.target.value === '' ? '' : parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-1">Total parures (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editData.tarif_parures_total}
                      onChange={(e) => setEditData({...editData, tarif_parures_total: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section Consommables */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-bold text-yellow-900 mb-3">📦 Consommables</h3>
                <div>
                  <label className="block text-sm font-medium text-yellow-800 mb-1">Total consommables (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editData.total_consommables}
                    onChange={(e) => setEditData({...editData, total_consommables: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>
              
              {/* Section Prestations */}
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h3 className="font-bold text-pink-900 mb-3">🔧 Prestations supplémentaires</h3>
                <div>
                  <label className="block text-sm font-medium text-pink-800 mb-1">Total prestations (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editData.total_prestations}
                    onChange={(e) => setEditData({...editData, total_prestations: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              </div>
              
              {/* Section Ajustement */}
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="font-bold text-orange-900 mb-3">⚖️ Ajustement manuel</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-1">Montant ajustement (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editData.ajustement}
                      onChange={(e) => setEditData({...editData, ajustement: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="+ ou -"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-1">Commentaire</label>
                    <input
                      type="text"
                      value={editData.commentaire_ajustement}
                      onChange={(e) => setEditData({...editData, commentaire_ajustement: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="Raison de l'ajustement"
                    />
                  </div>
                </div>
              </div>
              
              {/* Total calculé */}
              <div className="bg-gray-900 text-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">NOUVEAU TOTAL</span>
                  <div className="text-right">
                    <span className="text-3xl font-bold">{calculateTotal(editData)}€</span>
                    {/* Indicateur de différence +X€ ou -X€ */}
                    {parseFloat(calculateDifference(editData)) !== 0 && (
                      <span className={`ml-3 text-xl font-bold ${
                        parseFloat(calculateDifference(editData)) > 0 
                          ? 'text-green-400' 
                          : 'text-red-400'
                      }`}>
                        {parseFloat(calculateDifference(editData)) > 0 ? '+' : ''}{calculateDifference(editData)}€
                      </span>
                    )}
                  </div>
                </div>
                {editData.ajustement !== 0 && (
                  <p className="text-gray-400 text-sm mt-1">
                    Dont ajustement: {editData.ajustement > 0 ? '+' : ''}{editData.ajustement}€
                    {editData.commentaire_ajustement && ` (${editData.commentaire_ajustement})`}
                  </p>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end gap-3">
              <button
                onClick={() => setEditModal({ open: false, client: null, logement: null })}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={saveEdits}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {saving ? '⏳ Enregistrement...' : '✅ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBillingSettings && (
        <BillingSettingsModal
          appConfig={billingConfig || appConfig}
          onClose={() => setShowBillingSettings(false)}
          onSaved={(saved) => {
            // Mettre à jour immédiatement l'état local + rafraîchir le contexte global
            setBillingConfig(prev => ({ ...(prev || {}), ...(saved || {}) }));
            if (refreshConfig) refreshConfig();
          }}
        />
      )}
    </div>
  );
};

export default FacturationRecap;
/* Deployment trigger - Sun Jan  4 16:59:10 UTC 2026 */
